/**
 * Public SKU catalog source for the landing pricing block (data-driven prices).
 *
 * The landing pricing block is a PUBLIC pre-auth surface, so it cannot call the
 * auth-gated /api/users/credits/skus/ route. It reads the PUBLIC catalog at
 * `/api/public/sku-catalog/` — published catalog prices
 * resolved in Django, never a hardcoded euro literal in the component
 * (pricing-plan.md R1).
 *
 * Prices remain readable when purchases are disabled; checkoutAvailable is
 * separate from price availability and must explicitly authorize purchase CTAs.
 * Without checkout, published database prices do not depend on Stripe.
 * NO-FALLBACK (CLAUDE.md): when the selected catalog source is unavailable
 * the endpoint returns `available: false` (or an empty
 * SKU list). This loader surfaces that honestly as `available: false` so the UI
 * renders an explicit "price loads at checkout" / unavailable state — it NEVER
 * fabricates a price.
 *
 */
import { apiClient } from "@/api/client";

/** Public, pre-auth SKU as served by /api/public/sku-catalog/. */
export interface PublicSku {
  id: string;
  catalogVersion: string;
  name: string;
  priceEur: number;
  currency: string;
  cadence: "one_off" | "annual";
  perPlot: boolean;
  unlocks: string[];
  teaser: string;
  includesYearOfPackage?: boolean;
}

/** Typed public catalog provider contract (LandingMarketingSource: SKU slice). */
export interface PublicSkuCatalog {
  available: boolean;
  /** Price visibility does not authorize a purchase; only an explicit true does. */
  checkoutAvailable?: boolean;
  skus: PublicSku[];
}

const UNAVAILABLE: PublicSkuCatalog = { available: false, skus: [] };

const isNonEmptyText = (value: unknown): value is string =>
  typeof value === "string" && value.trim().length > 0;

const isSupportedCurrency = (value: unknown): value is string => {
  if (typeof value !== "string" || !/^[A-Za-z]{3}$/.test(value)) return false;
  try {
    new Intl.NumberFormat("en", {
      style: "currency",
      currency: value.toUpperCase(),
    }).format(0);
    return true;
  } catch {
    return false;
  }
};

const hasValidSkuIdentity = (v: Record<string, unknown>): boolean =>
  isNonEmptyText(v.id) &&
  isNonEmptyText(v.catalogVersion) &&
  Number.isFinite(Date.parse(v.catalogVersion)) &&
  isNonEmptyText(v.name);

const hasValidSkuPrice = (v: Record<string, unknown>): boolean =>
  typeof v.priceEur === "number" &&
  Number.isFinite(v.priceEur) &&
  v.priceEur >= 0 &&
  isSupportedCurrency(v.currency) &&
  (v.cadence === "one_off" || v.cadence === "annual") &&
  typeof v.perPlot === "boolean";

const hasValidSkuCopy = (v: Record<string, unknown>): boolean =>
  Array.isArray(v.unlocks) &&
  v.unlocks.every(isNonEmptyText) &&
  isNonEmptyText(v.teaser) &&
  (v.includesYearOfPackage === undefined ||
    typeof v.includesYearOfPackage === "boolean");

export const isPublicSku = (value: unknown): value is PublicSku => {
  if (typeof value !== "object" || value === null) return false;
  const v = value as Record<string, unknown>;
  return hasValidSkuIdentity(v) && hasValidSkuPrice(v) && hasValidSkuCopy(v);
};

export function assertPublicSkuCatalog(value: unknown): PublicSkuCatalog {
  if (typeof value !== "object" || value === null) {
    throw new Error("Public SKU catalog response must be an object");
  }
  const body = value as Record<string, unknown>;
  if (body.checkoutAvailable !== undefined && typeof body.checkoutAvailable !== "boolean") {
    throw new Error("Public SKU catalog has an invalid checkout availability");
  }
  if (body.available === false) {
    if (!Array.isArray(body.skus) || body.skus.length !== 0) {
      throw new Error(
        "Unavailable public SKU catalog must carry an empty SKU list",
      );
    }
    return UNAVAILABLE;
  }
  if (body.available !== true || !Array.isArray(body.skus)) {
    throw new Error("Public SKU catalog response has an invalid schema");
  }
  if (!body.skus.every(isPublicSku)) {
    throw new Error("Public SKU catalog contains an invalid SKU");
  }
  const skuIds = body.skus.map((sku) => sku.id);
  if (new Set(skuIds).size !== skuIds.length) {
    throw new Error("Public SKU catalog contains duplicate SKU ids");
  }
  if (body.skus.length === 0) return UNAVAILABLE;
  return {
    available: true,
    checkoutAvailable: body.checkoutAvailable === true,
    skus: body.skus,
  };
}

/**
 * Fetch the public SKU catalog through the shared apiClient so the base URL is
 * resolved consistently with the rest of the app (dev proxy in `vite dev`, the
 * configured VITE_API_BASE_URL in built bundles). The endpoint is public — no
 * bearer is required; apiClient only attaches a token when one is present.
 *
 * A valid `available:false` response is an honest unavailable state. Transport
 * and schema failures throw so React Query can expose the distinct error/retry
 * state instead of misreporting a broken response as an empty catalog.
 */
export async function loadPublicSkuCatalog(): Promise<PublicSkuCatalog> {
  const response = await apiClient.get<unknown>("/api/public/sku-catalog/", {
    skipErrorLogging: true,
    skipAuth401: true,
  });
  return assertPublicSkuCatalog(response.data);
}

/** Whole-euro prices show no decimals; fractional prices show two. */
const fractionDigitsFor = (amount: number): number =>
  Number.isInteger(amount) ? 0 : 2;

/** Format a SKU price using the catalog's own currency (no euro literal). */
export function formatSkuPrice(sku: PublicSku, locale: string): string {
  const fractionDigits = fractionDigitsFor(sku.priceEur);
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: sku.currency.toUpperCase(),
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: 2,
  }).format(sku.priceEur);
}
